/**
 * Centralised route-path constants for the entire application.
 *
 * Every `<Link>`, `navigate()`, and `createBrowserRouter` path references
 * this object so route strings are never duplicated. Grouped by feature
 * domain: AUTH, INVITE, INSTITUTE, UNIT, QUESTIONS, ANSWERS, TESTS,
 * TEST_RUN, SECTIONS, CLASSES, ADMIN, STUDENT, TAGS, and IMPORT.
 */
export const ROUTES = {
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    RESET_PASSWORD: '/reset-password',
  },
  INVITE: {
    ACCEPT: '/invite/accept',
  },
  INSTITUTE: {
    LIST: '/institutes',
    DETAIL: '/institutes/:id',
    CREATE: '/institutes/new',
    EDIT: '/institutes/:id/edit',
  },
  UNIT: {
    LIST: '/units',
    DETAIL: '/units/:id',
    CREATE: '/units/new',
    EDIT: '/units/:id/edit',
    MODULE_DETAIL: '/units/:unitId/modules/:moduleId',
  },
  QUESTIONS: {
    LIST: '/questions',
    DETAIL: '/questions/:id',
    CREATE: '/questions/new',
    EDIT: '/questions/:id/edit',
    IMPORT: '/questions/import',
  },
  ANSWERS: {
    LIST: '/answers',
    CREATE: '/answers/new',
    EDIT: '/answers/:id/edit',
  },
  // ASSOCIATED_ANSWERS routes removed in v4.0 — answer options are now managed
  // as the `answerOptions` array field directly on the Question document.
  TESTS: {
    LIST: '/tests',
    DETAIL: '/tests/:id',
    CREATE: '/tests/new',
    EDIT: '/tests/:id/edit',
    /** v2.0 — SuperUser test run (legacy redirect → TestRunnerPage) */
    RUN: '/tests/:testId/run',
    /** v3.0 — Unified runner: /tests/:testId/runner/:mode */
    RUNNER: '/tests/:testId/runner/:mode',
  },
  /** v3.0 — Unified test run report and history */
  TEST_RUN: {
    REPORT: '/test-runs/:runId/report',
    HISTORY: '/test-runs/history',
  },
  SECTIONS: {
    CREATE: '/tests/:testId/sections/new',
    EDIT: '/tests/:testId/sections/:sectionId/edit',
  },
  CLASSES: {
    LIST: '/classes',
    DETAIL: '/classes/:id',
    CREATE: '/classes/new',
    EDIT: '/classes/:id/edit',
    MODULE_DETAIL: '/classes/:classId/modules/:moduleId',
    TEST_RESULTS: '/classes/:classId/tests/:classTestId/results',
  },
  TAGS: {
    LIST: '/tags',
  },
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    ENROLLMENT: '/student/classes',
    CLASS: '/student/classes/:classId',
    EXAM: '/student/exam/:testSessionId',
    RESULTS: '/student/results/:performanceId',
    PRACTICE: '/student/practice/:testId',
    /** v2.0 — paginated test history */
    HISTORY: '/student/history',
  },
  ADMIN: {
    DASHBOARD: '/admin',
    USERS: '/admin/users',
    USER_PROFILE: '/admin/users/:uid/profile',
    INSTITUTES: '/admin/institutes',
    UNITS: '/admin/units',
    ANALYTICS: '/admin/analytics',
    TAGS: '/admin/tags',
    TESTS: '/admin/tests',
    INVITATIONS: '/admin/invitations',
    /** JSON bulk-import page */
    IMPORT: '/admin/import',
    /** Spreadsheet (CSV/XLSX) question import — available to all staff roles */
    IMPORT_SPREADSHEET: '/questions/import',
  },
  UNAUTHORIZED: '/unauthorized',
} as const
